import React from "react";
import { Box, Typography, Tooltip, Stack, IconButton } from "@mui/material";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AnimatedNumber from "./Animations/Number";
import { useReferentialContext } from "../Context";
import formatters from "./Formatters";
import { 
    getStatisticalGradientClass, 
    getStatisticalDeltaGradientClass,
    GradientMetric,
    intToOrdinal
} from "./utils";

/**
 * Metric interface for StatsBlock component
 * Extends GradientMetric with display fields
 * Allows additional properties for compatibility with GameMetrics
 */
export interface StatsBlockMetric extends GradientMetric {
    label?: string | null;
    desc?: string | null;
    [key: string]: any;
}

/**
 * Props for the StatsBlock component
 */
export interface StatsBlockProps {
    /** The value to display */
    value: number;
    /** The label to show below the value */
    label: string;
    /** The metric code for gradient calculation */
    metricCode: string;
    /** The number of games for statistical significance */
    games?: number;
    /** The metric object for gradient calculation */
    metric?: StatsBlockMetric;
    /** Custom formatter function for the value */
    formatter?: (value: number) => string;
    /** Whether to use delta gradient calculation (for GD@15, CSD@15, etc.) */
    isDelta?: boolean;
    /** Custom gradient class override */
    gradientClass?: string;
    /** Type of gradient: 'percentage', 'rating', 'delta_percentage', 'delta_rating' */
    gradientType?: 'percentage' | 'rating' | 'delta_percentage' | 'delta_rating';
    /** Custom expected average to override metric.expected_avg */
    custom_avg?: number;
    /** Custom expected standard deviation to override metric.expected_std */
    custom_std?: number;
}

/**
 * Reusable stats block component that displays a formatted value with label and gradient styling
 * @param props - The component props
 * @returns JSX element for the stats block
 */
export function StatsBlock({
    value,
    label,
    metricCode: _metricCode,
    games,
    metric,
    formatter: customFormatter,
    isDelta = false,
    gradientClass,
    gradientType,
    custom_avg,
    custom_std
}: StatsBlockProps): JSX.Element {
    const context = useReferentialContext();
    
    // Determine the formatter to use
    // If custom formatter provided, use it
    // Otherwise, if isDelta is true or metric type indicates delta, use delta formatter
    // Otherwise, use default formatter
    const formatter = customFormatter || (
        isDelta || (metric as any)?.type?.startsWith('delta_') 
            ? formatters.delta 
            : (val) => val.toFixed(1)
    );
    
    // Track if custom values are being used
    const usingCustomAvg = custom_avg !== undefined;
    const usingCustomStd = custom_std !== undefined;
    
    // Create effective metric with custom values if provided
    let effectiveMetric = metric;
    if (usingCustomAvg || usingCustomStd) {
        if (metric) {
            // If metric exists, create a new object with custom values
            effectiveMetric = {
                ...metric,
                expected_avg: custom_avg !== undefined ? custom_avg : metric.expected_avg,
                expected_std: custom_std !== undefined ? custom_std : metric.expected_std
            };
        } else if (custom_avg !== undefined && custom_std !== undefined) {
            // If no metric but both custom values provided, create a minimal metric object
            effectiveMetric = {
                code: _metricCode,
                expected_avg: custom_avg,
                expected_std: custom_std
            } as any;
        }
    }
    
    // Use effective metric for calculations
    const finalMetric = effectiveMetric;
    
    // Determine which function is used for gradient calculation
    let gradientFunctionUsed: string;
    if (gradientClass) {
        gradientFunctionUsed = 'Custom (provided)';
    } else if (isDelta) {
        gradientFunctionUsed = 'getStatisticalDeltaGradientClass';
    } else {
        gradientFunctionUsed = 'getStatisticalGradientClass';
    }
    
    // Calculate gradient class if not provided
    const calculatedGradientClass = gradientClass || (
        isDelta 
            ? getStatisticalDeltaGradientClass(value, finalMetric, games)
            : getStatisticalGradientClass(value, finalMetric, games)
    );
    
    // Determine actual gradient type used by examining the metric and logic path
    let actualGradientType: string;
    if (gradientType) {
        // If explicitly provided, use it
        actualGradientType = gradientType;
    } else if (isDelta) {
        // Delta metrics
        if (finalMetric?.available_for === 'ratings') {
            actualGradientType = 'delta_rating';
        } else {
            actualGradientType = 'delta_percentage';
        }
    } else {
        // Non-delta metrics - check if treated as rating internally
        if (finalMetric?.available_for === 'ratings') {
            actualGradientType = 'rating';
        } else {
            actualGradientType = 'percentage';
        }
    }
    
    // Calculate z-score for debug mode
    const zScore = finalMetric?.expected_avg !== undefined && 
                    finalMetric?.expected_avg !== null && 
                    finalMetric?.expected_std !== undefined &&
                    finalMetric?.expected_std !== null
        ? (value - finalMetric.expected_avg) / finalMetric.expected_std
        : null;
    
    // Determine if games-based calculation applies
    const usesGamesBased = !!(games && games >= 150 && finalMetric?.available_for !== 'ratings');
    
    // Calculate bounds if games-based calculation is used
    let lowerBound: number | null = null;
    let upperBound: number | null = null;
    if (usesGamesBased && finalMetric?.expected_avg !== undefined && finalMetric?.expected_avg !== null && 
        finalMetric?.expected_std !== undefined && finalMetric?.expected_std !== null) {
        const standardError = finalMetric.expected_std / Math.sqrt(games!);
        const criticalValue = 1.96;
        lowerBound = finalMetric.expected_avg - (criticalValue * standardError);
        upperBound = finalMetric.expected_avg + (criticalValue * standardError);
    }

    // Generate debug information text for copying
    const generateDebugText = () => {
        const lines = [];
        lines.push(`Label: ${label}`);
        lines.push(`Value: ${value !== null && value !== undefined ? formatter(value) : 'N/A'}`);
        
        if (finalMetric?.expected_avg !== undefined && finalMetric.expected_avg !== null) {
            lines.push(`Expected Avg: ${formatter(finalMetric.expected_avg)}${usingCustomAvg ? ' (Custom)' : ''}`);
        } else {
            lines.push(`Expected Avg: Not available`);
        }
        
        if (finalMetric?.expected_std !== undefined && finalMetric.expected_std !== null) {
            lines.push(`Expected Std: ${formatter(finalMetric.expected_std)}${usingCustomStd ? ' (Custom)' : ''}`);
        } else {
            lines.push(`Expected Std: Not available`);
        }
        
        if (zScore !== null) {
            lines.push(`Z-Score: ${zScore.toFixed(3)}`);
        } else {
            lines.push(`Z-Score: Cannot calculate (no metric)`);
        }
        
        if (games !== undefined) {
            lines.push(`Games: ${games}`);
        }
        
        lines.push(`Gradient Type: ${actualGradientType}`);
        lines.push(`Gradient Class: ${calculatedGradientClass || 'none'}`);
        lines.push(`Function: ${gradientFunctionUsed}`);
        lines.push(`Uses Games-Based: ${usesGamesBased ? 'Yes' : 'No'}`);
        
        if (usesGamesBased && lowerBound !== null && upperBound !== null) {
            lines.push(`Lower Bound: ${formatter(lowerBound)}`);
            lines.push(`Upper Bound: ${formatter(upperBound)}`);
            if (value !== null && value !== undefined) {
                lines.push(`Value vs Bounds: ${value < lowerBound ? 'Below' : value < upperBound ? 'Within' : 'Above'}`);
            }
        }
        
        if (finalMetric?.available_for) {
            lines.push(`Metric Type: ${finalMetric.available_for}`);
        }
        
        return lines.join('\n');
    };
    
    const handleCopyDebugInfo = (event: React.MouseEvent) => {
        event.stopPropagation();
        const debugText = generateDebugText();
        navigator.clipboard.writeText(debugText);
    };

    // Generate debug tooltip content - always show in debug mode
    const debugTooltip = context.showDebugMode ? (
        <Stack spacing={0.5}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <Typography variant="body2" fontWeight="bold">
                    Debug Info
                </Typography>
                <IconButton 
                    size="small" 
                    onClick={handleCopyDebugInfo}
                    sx={{ 
                        padding: '2px',
                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                    }}
                >
                    <ContentCopyIcon sx={{ fontSize: '14px', color: 'white' }} />
                </IconButton>
            </Box>
            <Typography variant="body2" fontWeight="bold">
                Value: {value !== null && value !== undefined ? formatter(value) : 'N/A'}
            </Typography>
            {finalMetric?.expected_avg !== undefined && finalMetric.expected_avg !== null ? (
                <Typography variant="body2" fontWeight="bold">
                    Expected Avg: {formatter(finalMetric.expected_avg)} {usingCustomAvg && '(Custom)'}
                </Typography>
            ) : (
                <Typography variant="body2" fontWeight="bold" color="warning.main">
                    Expected Avg: Not available
                </Typography>
            )}
            {finalMetric?.expected_std !== undefined && finalMetric.expected_std !== null ? (
                <Typography variant="body2" fontWeight="bold">
                    Expected Std: {formatter(finalMetric.expected_std)} {usingCustomStd && '(Custom)'}
                </Typography>
            ) : (
                <Typography variant="body2" fontWeight="bold" color="warning.main">
                    Expected Std: Not available
                </Typography>
            )}
            {lowerBound !== null && upperBound !== null && (
                <>
                    <Typography variant="body2" fontWeight="bold">
                        Lower Bound: {formatter(lowerBound)}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                        Upper Bound: {formatter(upperBound)}
                    </Typography>
                    {value !== null && value !== undefined && (
                        <Typography variant="body2" fontWeight="bold">
                            Value vs Bounds: {value < lowerBound ? 'Below' : value < upperBound ? 'Within' : 'Above'}
                        </Typography>
                    )}
                </>
            )}
            {zScore !== null ? (
                <Typography variant="body2" fontWeight="bold">
                    Z-Score: {zScore.toFixed(3)}
                </Typography>
            ) : (
                <Typography variant="body2" fontWeight="bold" color="warning.main">
                    Z-Score: Cannot calculate (no metric)
                </Typography>
            )}
            {games !== undefined && (
                <Typography variant="body2" fontWeight="bold">
                    Games: {games}
                </Typography>
            )}
            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.7rem', opacity: 0.8 }}>
                Gradient Type: {actualGradientType}
            </Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.7rem', opacity: 0.8 }}>
                Gradient Class: {calculatedGradientClass || 'none'}
            </Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.7rem', opacity: 0.8 }}>
                Function: {gradientFunctionUsed}
            </Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.7rem', opacity: 0.8 }}>
                Uses Games-Based: {usesGamesBased ? 'Yes' : 'No'}
            </Typography>
            {finalMetric?.available_for && (
                <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.7rem', opacity: 0.7 }}>
                    Metric Type: {finalMetric.available_for}
                </Typography>
            )}
        </Stack>
    ) : '';

    // Generate tooltip content for normal mode (label + description)
    const normalTooltip = (
        <Stack spacing={0.5}>
            <Typography variant="body2" fontWeight="bold">
                {metric?.label || label}
            </Typography>
            {metric?.desc && (
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {metric.desc}
                </Typography>
            )}
        </Stack>
    );

    const content = (
        <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography 
                variant="body2" 
                sx={{ fontWeight: 600, color: "white", fontSize: "0.8rem" }}
                className={calculatedGradientClass}
            >
                <AnimatedNumber 
                    value={value} 
                    format={formatter}
                    fallback="--"
                />
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "0.75rem" }}>
                {label}
            </Typography>
        </Box>
    );

    // Wrap with tooltip - debug mode shows detailed info, normal mode shows label + description
    if (context.showDebugMode) {
        return (
            <Tooltip title={debugTooltip} arrow>
                {content}
            </Tooltip>
        );
    }

    // Always show tooltip with label and description in normal mode
    return (
        <Tooltip title={normalTooltip} arrow>
            {content}
        </Tooltip>
    );
}

export interface StatsBlockRankedProps extends StatsBlockProps {
    rank?: number | null;
    maxRank?: number;
}

export function getRankColor(rank: number, maxRank: number): string {
    if (rank === 1) return '#2196F3';
    if (rank <= 3) return '#4CAF50';
    const half = Math.ceil(maxRank / 2);
    const bottomThreshold = maxRank - 2;
    if (rank <= half) return '#FFA726';
    if (rank < bottomThreshold) return '#FF8A65';
    return '#F44336';
}

const rankGradientMap: Record<string, string> = {
    '#2196F3': 'gradient-5',
    '#4CAF50': 'gradient-4',
    '#FFA726': 'gradient-3',
    '#FF8A65': 'gradient-2',
    '#F44336': 'gradient-1',
};

export function StatsBlockRanked({ rank, maxRank, ...statsBlockProps }: StatsBlockRankedProps): JSX.Element {
    const rankColor = rank != null ? getRankColor(rank, maxRank || rank) : undefined;
    const gradientClass = rankColor ? rankGradientMap[rankColor] || undefined : undefined;
    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            width: '70px',
        }}>
            <StatsBlock
                {...statsBlockProps}
                {...(gradientClass && { gradientClass })}
            />
            {rank != null && (
                <Typography
                    sx={{
                        color: rankColor,
                        fontSize: '0.6rem',
                        fontWeight: 600,
                        lineHeight: 1,
                    }}
                >
                    {intToOrdinal(rank)}
                </Typography>
            )}
        </Box>
    );
}
